import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import React from 'react';
import s from './solution-info.scss';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { composer } from '../../../../component-decorators/composer';
import { CarouselPreviewWrapper } from '../../preview-elements/carousel-wrapper';
import { Carousel, Box, Proportion } from '@wix/design-system';
import { getClientImageURL } from '../../../../common/getClientImageURL';
import { biLogger } from '../../../../bi';
import { appMarketAppPageAppGalleryClick } from '@wix/bi-logger-market/v2';
import type { WebSolution } from '@wix/ambassador-marketplace/types';

interface IAppName extends Partial<WithTranslationProps> {
  appName: string;
}
export interface IPreviewProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  previews: { src: string }[];
  videos?: string[];
  appId?: string;
}

export const dtoToPreviewProps = (solutionDto: WebSolution): IPreviewProps => {
  const images =
    solutionDto.assetsMap.images || solutionDto.assetsMap.screenshots;
  const videos =
    solutionDto.assetsMap &&
    solutionDto.assetsMap.galleryVideos &&
    solutionDto.assetsMap.galleryVideos.assets
      .filter((v) => v.mediaType === 'YOUTUBE')
      .map((v) => v.url);
  return {
    previews:
      images &&
      images.assets
        .filter((asset) => asset.mediaType === 'IMAGE')
        .map((asset) => {
          return {
            src: asset.url,
          };
        }),
    videos,
    appId: solutionDto.baseInfo.id,
  };
};
const landscape = 4 / 3;
export const SolutionInfoPreview = composer()
  .withExperiments()
  .withTranslation()
  .compose((props: IPreviewProps & IAppName) => {
    return (
      <Box>
        <CarouselPreviewWrapper previews={props.previews}>
          <Carousel
            buttonSkin="inverted"
            dataHook="preview-carousel"
            className={s.previewContainer}
            beforeChange={() =>
              biLogger.report(
                appMarketAppPageAppGalleryClick({
                  app_id: props.appId,
                  tag_name: 'app_page',
                }),
              )
            }
          >
            {props.videos && props.videos.length > 0 && (
              <Proportion aspectRatio={landscape}>
                {' '}
                <Video videoUrl={props.videos[0]} />{' '}
              </Proportion>
            )}

            {props.previews &&
              props.previews.map((img, index) => (
                <Proportion
                  data-hook={`hook-${img.src}`}
                  aspectRatio={landscape}
                >
                  <Image
                    imgUrl={img.src}
                    alt={`${props.appName} ${props.t(
                      'solution.page.preview.image.gallery.alt',
                    )} ${index}`}
                  />{' '}
                </Proportion>
              ))}
          </Carousel>
        </CarouselPreviewWrapper>
      </Box>
    );
  });

function Video({ videoUrl }) {
  return (
    <iframe
      width="100%"
      height="100%"
      src={`${videoUrl}?rel=0`}
      className={s.carouselVideo}
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
    ></iframe>
  );
}

function Image({ imgUrl, alt }) {
  const mobileBreakpoint = 600;
  const getCarosualImageSrc = (width, height) =>
    getClientImageURL(imgUrl, {
      originalWidth: 1000,
      originalHeight: 720,
      toWidth: width,
      toHeight: height,
    });
  const mobileSrc = getCarosualImageSrc(430, 330);
  const desktopSrc = getCarosualImageSrc(605, 465);
  return (
    <picture key={imgUrl}>
      <source media={`(max-width: ${mobileBreakpoint}px)`} srcSet={mobileSrc} />
      <img
        alt={alt}
        style={{ width: '100%', height: 'auto' }}
        src={desktopSrc}
      />
    </picture>
  );
}
